import { z } from 'zod'

/**
 * The zone a household's days begin and end in.
 *
 * An IANA identifier rather than an offset, because an offset is the part that
 * rots: `+01:00` is Copenhagen for seven months of the year and wrong for the
 * other five, and which five moves by legislation. The name survives that; the
 * runtime's own tz database resolves it to whatever the offset is on the day
 * being asked about.
 */
export const TIMEZONE_SETTING_KEY = 'timezone'

/**
 * What a household that has never been asked is stamped in.
 *
 * Left as the fallback for an absent row rather than written at sign-up, so that
 * a household with no answer on file is not on record as having given one — the
 * same reason the fiscal year end is not written until somebody sets it. The
 * product says which zone it used rather than letting the absence pass as a
 * choice.
 */
export const DEFAULT_TIMEZONE = 'UTC'

/**
 * Whether the runtime can resolve this name to a zone.
 *
 * Asked of the runtime rather than of a list, because a list of zones is a thing
 * to be versioned and a tz database is a thing that is already maintained:
 * zones are added, renamed and merged, and a hand-kept list would start refusing
 * households that Node and every browser accept.
 */
export function isTimezone(name: string): boolean {
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: name })
    return true
  } catch {
    return false
  }
}

export const timezoneSchema = z
  .string()
  .trim()
  .refine(isTimezone, { message: 'must be an IANA time zone name, such as Europe/Copenhagen' })

/**
 * The calendar day an instant falls on, in a named zone.
 *
 * The locale is named rather than inherited so the host's cannot choose the
 * digits, and the parts are read by name so a locale that orders or separates
 * them differently cannot change the answer. The same reasoning, and the same
 * shape, as the browser's `dateIn` — they are two runtimes answering one
 * question, and neither may take the other's word for the household's zone.
 */
export function dateIn(at: Date, zone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at)
  const of = (type: string): string => parts.find((part) => part.type === type)?.value ?? ''
  return `${of('year')}-${of('month')}-${of('day')}`
}

/**
 * The day it is for this household, which is the only thing a stamp on its own
 * records should ever be.
 *
 * Separate from `toDateString` deliberately. That one formats a UTC midnight
 * somebody else's arithmetic built, and has to go on doing exactly that; this
 * one answers a question about a household, and there is no host whose zone is
 * the right answer to it.
 */
export function todayFor(at: Date, zone: string | undefined): string {
  return dateIn(at, zone ?? DEFAULT_TIMEZONE)
}
